import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getRef, getAff } from '@/lib/landingVariants';

type Plan = 'monthly' | 'yearly' | 'family-monthly' | 'family-yearly';

export function useCheckout(onSwitched?: () => void) {
  const [loading, setLoading] = useState(false);

  const openCheckout = useCallback(async (plan: Plan) => {
    setLoading(true);
    try {
      const ref = getRef();
      const aff = getAff();
      const body: Record<string, unknown> = { plan };
      if (ref) body.ref = ref;
      if (aff) body.aff = aff;
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body,
      });
      if (error) throw error;

      if (data?.noop) {
        toast("You're already on this plan.");
        setLoading(false);
        return;
      }

      if (data?.resumed) {
        toast.success('Subscription resumed', {
          description: 'No charge today. Your plan will renew as scheduled.',
        });
        onSwitched?.();
        setLoading(false);
        return;
      }

      if (data?.switched) {
        toast.success('Plan updated', {
          description: 'Prorated invoice on the way from Lemon Squeezy.',
        });
        onSwitched?.();
        setLoading(false);
        return;
      }

      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (err: any) {
      console.error('Checkout error:', err);
      toast.error('Could not complete request', {
        description: err?.message || 'Please try again in a moment.',
      });
      setLoading(false);
    }
  }, [onSwitched]);

  return { openCheckout, loading };
}
